import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { useAlert, type AlertItem } from '@/context/AlertContext.tsx';

const icons = {
  success: CheckCircle,
  error: AlertCircle,
  info: Info,
  warning: AlertTriangle,
};

const colors = {
  success: 'bg-green-600 text-white',
  error: 'bg-red-600 text-white',
  info: 'bg-blue-600 text-white',
  warning: 'bg-yellow-500 text-black',
};

function ariaRoleFor(severity: AlertItem['severity']) {
  return severity === 'error' || severity === 'warning' ? 'alert' : 'status';
}

function ariaLiveFor(severity: AlertItem['severity']) {
  return severity === 'error' || severity === 'warning' ? 'assertive' : 'polite';
}

function AlertToast({ item }: { item: AlertItem }) {
  const { t } = useTranslation();
  const { dismissAlert } = useAlert();
  const Icon = icons[item.severity];

  useEffect(() => {
    if (item.persistent) return;
    const timer = setTimeout(() => dismissAlert(item.id), item.durationMs);
    return () => clearTimeout(timer);
  }, [item.id, item.persistent, item.durationMs, dismissAlert]);

  return (
    <div
      role={ariaRoleFor(item.severity)}
      aria-live={ariaLiveFor(item.severity)}
      aria-atomic="true"
      className="animate-in slide-in-from-top-2 fade-in duration-300"
    >
      <div
        className={`flex items-center gap-3 rounded-lg px-4 py-3 shadow-lg ${colors[item.severity]}`}
      >
        <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span className="text-sm font-medium">{item.message}</span>
        <button
          type="button"
          onClick={() => dismissAlert(item.id)}
          className="ml-2 shrink-0 rounded-full p-0.5 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 transition-colors"
          aria-label={t('common.dismissAlert')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function Alert() {
  const { alerts } = useAlert();

  if (alerts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
      {alerts.map((item) => (
        <AlertToast key={item.id} item={item} />
      ))}
    </div>
  );
}
